/**
 * 🟦 V14.2 — LyonHeaderToolbar: 7 botones extraídos del header del Lyon.
 *
 * Componente PURO. Recibe:
 *  - flags: es, multiStep, canSaveRecipe, hasAppContext, inferenceLoading, importBusy
 *  - data: recipes (length), memory ({lastNodeId, lastPatches, lastNodeContext})
 *  - callbacks: onSaveRecipe, onInferStyle, onOpenRecipes, onOpenMarketplace,
 *               onOpenStylePacks, onOpenOnboarding, onImportRecipe
 *
 * Mantiene EXACTAMENTE los mismos data-testid, iconos, clases Tailwind y
 * tooltips del código original (líneas 1400-1491 del archivo monolítico).
 * Esto es crítico: cualquier cambio de testid rompería el test suite.
 */
import {
  BookmarkPlus, Loader2, Wand2, BookOpen, Store, Layers, Sparkles, Upload, FolderOpen,
} from 'lucide-react';

const LyonHeaderToolbar = ({
  es,
  multiStep,
  canSaveRecipe,
  hasAppContext,
  inferenceLoading,
  importBusy,
  recipes,
  memory,
  onSaveRecipe,
  onInferStyle,
  onOpenRecipes,
  onOpenMarketplace,
  onOpenProyectos,
  onOpenStylePacks,
  onOpenOnboarding,
  onImportRecipe,
}) => {
  const recipesCount = (recipes && recipes.length) || 0;
  const patchCount = (memory?.lastPatches?.length) || 0;

  return (
    <div
      className="flex items-center gap-1.5 overflow-x-auto overflow-y-hidden lyon-toolbar-scroll pb-1.5 -mx-1 px-1"
      data-testid="lyon-toolbar-scroll"
      style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(168,85,247,0.5) transparent' }}
    >
      {canSaveRecipe && (
        <button
          type="button"
          onClick={onSaveRecipe}
          data-testid="lyon-save-recipe-button"
          title={es
            ? `Guardar receta (${patchCount} pasos sobre <${memory?.lastNodeContext?.tag || 'nodo'}>)`
            : `Save recipe (${patchCount} steps on <${memory?.lastNodeContext?.tag || 'node'}>)`}
          className="shrink-0 whitespace-nowrap px-2 py-1 rounded-md bg-amber-900/40 hover:bg-amber-800/60 text-amber-200 hover:text-white border border-amber-500/40 transition-colors flex items-center gap-1 text-[10px] font-semibold"
        >
          <BookmarkPlus className="w-3 h-3" />
          {es ? 'Guardar receta' : 'Save recipe'}
        </button>
      )}

      <button
        type="button"
        onClick={onInferStyle}
        disabled={inferenceLoading}
        data-testid="lyon-style-inference-button"
        title={es ? 'Inferir el estilo visual del nodo seleccionado' : 'Infer visual style of selected node'}
        className="shrink-0 whitespace-nowrap px-2 py-1 rounded-md bg-fuchsia-900/40 hover:bg-fuchsia-800/60 text-fuchsia-200 hover:text-white border border-fuchsia-500/40 transition-colors flex items-center gap-1 text-[10px] font-semibold disabled:opacity-50"
      >
        {inferenceLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
        {es ? 'Inferir estilo' : 'Infer style'}
      </button>

      <button
        type="button"
        onClick={onOpenRecipes}
        data-testid="lyon-open-recipes-panel"
        title={es ? `Abrir panel de recetas (${recipesCount})` : `Open recipes panel (${recipesCount})`}
        className="shrink-0 whitespace-nowrap px-2 py-1 rounded-md bg-slate-800/60 hover:bg-slate-700/80 text-slate-300 hover:text-white border border-slate-600/40 transition-colors flex items-center gap-1 text-[10px] font-semibold"
      >
        <BookOpen className="w-3 h-3" />
        {es ? `Recetas (${recipesCount})` : `Recipes (${recipesCount})`}
      </button>

      <button
        type="button"
        onClick={onOpenMarketplace}
        data-testid="lyon-open-marketplace"
        title={es ? 'Marketplace de recetas IA' : 'AI Recipes Marketplace'}
        className="shrink-0 whitespace-nowrap px-2 py-1 rounded-md bg-cyan-900/40 hover:bg-cyan-800/60 text-cyan-200 hover:text-white border border-cyan-500/40 transition-colors flex items-center gap-1 text-[10px] font-semibold"
      >
        <Store className="w-3 h-3" />
        {es ? 'Marketplace' : 'Marketplace'}
      </button>

      {/* 🟦 CUADRO NEGRO — Botón "Mis Proyectos del Builder" (lista de apps del usuario). */}
      <button
        type="button"
        onClick={onOpenProyectos}
        data-testid="lyon-open-proyectos"
        title={es ? 'Mis Proyectos del Builder' : 'My Builder Projects'}
        className="shrink-0 whitespace-nowrap px-2 py-1 rounded-md bg-indigo-900/40 hover:bg-indigo-800/60 text-indigo-200 hover:text-white border border-indigo-500/40 transition-colors flex items-center gap-1 text-[10px] font-semibold"
      >
        <FolderOpen className="w-3 h-3" />
        {es ? 'Mis Proyectos' : 'My Projects'}
      </button>

      <button
        type="button"
        onClick={onOpenStylePacks}
        data-testid="lyon-open-stylepacks"
        title={es ? 'Style Packs IA' : 'AI Style Packs'}
        className="shrink-0 whitespace-nowrap px-2 py-1 rounded-md bg-fuchsia-900/40 hover:bg-fuchsia-800/60 text-fuchsia-200 hover:text-white border border-fuchsia-500/40 transition-colors flex items-center gap-1 text-[10px] font-semibold"
      >
        <Layers className="w-3 h-3" />
        {es ? 'Style Packs' : 'Style Packs'}
      </button>

      <button
        type="button"
        onClick={onOpenOnboarding}
        data-testid="lyon-open-onboarding"
        title={es ? 'Reabrir asistente de inicio' : 'Reopen onboarding wizard'}
        className="shrink-0 whitespace-nowrap px-2 py-1 rounded-md bg-purple-900/40 hover:bg-purple-800/60 text-purple-200 hover:text-white border border-purple-500/40 transition-colors flex items-center gap-1 text-[10px] font-semibold"
      >
        <Sparkles className="w-3 h-3" />
        {es ? 'Onboarding' : 'Onboarding'}
      </button>

      <button
        type="button"
        onClick={onImportRecipe}
        disabled={importBusy || !hasAppContext}
        data-testid="lyon-import-recipe-button"
        title={es ? 'Importar receta desde archivo' : 'Import recipe from file'}
        className="shrink-0 whitespace-nowrap px-2 py-1 rounded-md bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-200 hover:text-white border border-emerald-500/40 transition-colors flex items-center gap-1 text-[10px] font-semibold disabled:opacity-50"
      >
        {importBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        {es ? 'Importar' : 'Import'}
      </button>

      {multiStep && memory?.lastNodeId && (
        <span
          className="shrink-0 whitespace-nowrap text-[10px] text-fuchsia-400 font-mono"
          data-testid="lyon-memory-indicator"
          title={es ? `Memoria activa · ${patchCount} patches` : `Memory active · ${patchCount} patches`}
        >
          🧠 · {patchCount}p
        </span>
      )}
    </div>
  );
};

export default LyonHeaderToolbar;
